import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FilenameInput } from '@/components/ui/FilenameInput';
import { Button } from '@/components/ui/NewButton';
import { FileText, Files } from 'lucide-react';
import { FolderTree } from '@/components/folders/FolderTree';
import { useFolderTree, FolderTree as FolderTreeType } from '@/hooks/useFolders';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';

type GenerateBundleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundleTitle: string;
  matterSlug: string;
  /** Output shape chosen from the toolbar dropdown: false = one combined file, true = one file per volume. */
  splitPerVolume?: boolean;
  onGenerate: (filename: string, targetFolderGuid: string | null, splitPerVolume: boolean) => void;
  isGenerating?: boolean;
};

export const GenerateBundleDialog = ({ open, ...rest }: GenerateBundleDialogProps) => (
  <Dialog open={open} onOpenChange={rest.onOpenChange}>
    {open && <GenerateBundleDialogContent {...rest} />}
  </Dialog>
);

const GenerateBundleDialogContent = ({
  onOpenChange,
  bundleTitle,
  matterSlug,
  splitPerVolume = false,
  onGenerate,
  isGenerating,
}: Omit<GenerateBundleDialogProps, 'open'>) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const { folderTree, isLoading: foldersLoading, mutate: refreshFolders } = useFolderTree(matterSlug);
  // TODO(binders): flip US label to "Binders" once the backend "Bundles" folder is localized.
  const bundlesFolder = folderTree.find(f => f.name === 'Bundles');

  const [filename, setFilename] = useState(bundleTitle.replace(/[/\\]/g, ''));
  const [selectedFolderGuid, setSelectedFolderGuid] = useState<string | null>(bundlesFolder?.guid ?? null);
  const hasSlashes = /[/\\]/.test(filename);
  const perVolume = splitPerVolume;

  const handleGenerate = () => {
    if (!filename.trim() || hasSlashes) return;
    onGenerate(filename.trim(), selectedFolderGuid, splitPerVolume);
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{ui_labels.bundle_generate_pdf_title}</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">File Name</label>
          <FilenameInput
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder={ui_labels.bundle_filename_placeholder}
            extension=".pdf"
            autoFocus
          />
          {hasSlashes && <p className="text-xs text-red-500">File name cannot contain slashes</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Save Location</label>
          <p className="text-xs text-gray-400 mb-1">
            {selectedFolderGuid
              ? 'Saving to selected folder'
              : ui_labels.bundle_folder_autocreate}
          </p>
          <div className="border rounded-md max-h-72 overflow-y-auto p-2">
            {foldersLoading ? (
              <p className="text-xs text-gray-400 text-center py-4">Loading folders…</p>
            ) : (
              <FolderTree
                folders={folderTree}
                totalDocuments={0}
                matterSlug={matterSlug}
                onRefresh={refreshFolders}
                navigationMode="callback"
                onFolderSelect={(folder: FolderTreeType | null) => {
                  const guid = folder?.guid ?? null;
                  if (selectedFolderGuid === guid) {
                    if (!bundlesFolder) setSelectedFolderGuid(null);
                    return;
                  }
                  setSelectedFolderGuid(guid);
                }}
                selectedFolderGuid={selectedFolderGuid}
                showFolderActions={false}
                hideAllFiles
              />
            )}
          </div>
        </div>
        {perVolume && (
          <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
            <Files className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-xs text-gray-600">
              Produces a separate PDF for each volume instead of a single combined file.
            </p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onOpenChange(false)}
          text="Cancel"
          disabled={isGenerating}
        />
        <Button
          size="sm"
          onClick={handleGenerate}
          disabled={!filename.trim() || hasSlashes || isGenerating}
          loading={isGenerating}
          text={perVolume ? 'Generate Files' : 'Generate'}
          icon={perVolume ? Files : FileText}
        />
      </DialogFooter>
    </DialogContent>
  );
};
